<template>
  <div
    v-if="prepared.length > 0"
    class="front-page-asset-strip__band"
  >
    <v-row
      no-gutters
      align="center"
      class="front-page-asset-strip__row ma-0 pa-0"
    >
      <col12 class="pa-0 ma-0">
        <div class="front-page-asset-strip">
          <v-carousel
            v-model="carouselIndex"
            class="front-page-asset-strip__carousel"
            :height="stripCarouselHeight"
            :cycle="prepared.length > 1"
            :interval="carouselIntervalMs"
            :continuous="true"
            :show-arrows="prepared.length > 1"
            :hide-delimiters="prepared.length <= 1"
            hide-delimiter-background
            dark
            :aria-label="$t('frontpage.dataset_preview_title')"
          >
            <v-carousel-item
              v-for="item in prepared"
              :key="item.id"
              class="front-page-asset-strip__carousel-item"
            >
              <div class="front-page-asset-strip__slide-inner">
                <annotated-image
                  class="front-page-asset-strip__image"
                  :image_url="item.imageUrl"
                  :annotations="item.annotations"
                  :max_height="annotatedImageMaxHeightPx"
                  :zoom_enabled="false"
                  :skip_image_auth_token="true"
                  :suppress_annotation_context_menu="true"
                  :hide_annotation_ui="true"
                />
              </div>
            </v-carousel-item>
          </v-carousel>
        </div>
      </col12>
    </v-row>
  </div>
</template>

<script>
import AnnotatedImage from '@/components/AnnotatedImage'
import Col12 from '@/components/layout/Col12'
const DEFAULT_COLORS = [
  [249, 65, 68],
  [67, 170, 139],
  [39, 125, 161],
]

export default {
  name: 'FrontPageAssetStrip',
  components: { AnnotatedImage, Col12 },
  data() {
    return {
      assets: [],
      carouselIndex: 0,
      carouselIntervalMs: 5500,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
    }
  },
  computed: {
    prepared() {
      return this.assets.map((raw) => this.prepareItem(raw))
    },
    /** 50% of viewport — drives BboxCanvas scaling via AnnotatedImage.max_height */
    annotatedImageMaxHeightPx() {
      const h = this.viewportHeight || 800
      return Math.round(h * 0.7)
    },
    stripCarouselHeight() {
      return this.annotatedImageMaxHeightPx
    },
  },
  watch: {
    assets() {
      this.carouselIndex = 0
    },
  },
  mounted() {
    this.syncViewport()
    this._onResize = () => this.syncViewport()
    window.addEventListener('resize', this._onResize)
    this.loadAssets()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this._onResize)
  },
  methods: {
    syncViewport() {
      if (typeof window === 'undefined') {
        return
      }
      this.viewportHeight = window.innerHeight
    },
    mapAnnotation(annotation, index) {
      const labelRel = annotation.relationships && annotation.relationships.label
      const labelName = labelRel ? labelRel.name : '?'
      let colorValues = DEFAULT_COLORS[index % DEFAULT_COLORS.length]
      if (labelRel && labelRel.color) {
        const parts = String(labelRel.color).split(',').map((x) => parseInt(x.trim(), 10))
        if (parts.length >= 3 && parts.every((n) => !Number.isNaN(n))) {
          colorValues = parts.slice(0, 3)
        }
      }
      let coords = annotation.coords
      if (typeof coords === 'string') {
        coords = JSON.parse(coords)
      }
      const confRaw = annotation.certainty
      const conf = confRaw == null || confRaw === '' ? 1 : parseFloat(confRaw) || 1
      return {
        id: annotation.id,
        type: annotation.type === 1 ? 'box' : 'point',
        coords,
        label: labelName,
        conf,
        color_values: colorValues,
        show: true,
        selected: false,
        skip_zoom: true,
      }
    },
    prepareItem(asset) {
      const rel = asset.relationships || {}
      const rawList = rel.annotations || []
      const annotations = rawList.map((a, i) => this.mapAnnotation(a, i))
      const imageUrl = `/api/public/front-page-asset-image/${asset.id}`
      return {
        id: asset.id,
        imageUrl,
        annotations,
      }
    },
    async loadAssets() {
      const url = '/api/public/front-page-assets'
      try {
        const res = await fetch(url)
        const headerProject = res.headers.get('X-Front-Page-Showcase-Project-Id')
        const headerCount = res.headers.get('X-Front-Page-Showcase-Asset-Count')
        const headerStatus = res.headers.get('X-Front-Page-Showcase-Status')

        if (!res.ok) {
          console.warn('[FrontPageAssetStrip] fetch failed', {
            url,
            status: res.status,
            statusText: res.statusText,
            showcaseProjectId: headerProject,
            showcaseStatus: headerStatus,
          })
          return
        }

        const data = await res.json()
        if (!Array.isArray(data)) {
          console.warn('[FrontPageAssetStrip] unexpected JSON (expected array)', {
            url,
            type: typeof data,
            showcaseProjectId: headerProject,
          })
          return
        }

        const perAsset = data.map((a) => ({
          id: a.id,
          filename: a.filename,
          annotationCount: (a.relationships && a.relationships.annotations
            ? a.relationships.annotations.length
            : 0),
        }))

        console.log('[FrontPageAssetStrip] showcase load', {
          url,
          responseHeaders: {
            showcaseProjectId: headerProject,
            showcaseAssetCount: headerCount,
            showcaseStatus: headerStatus,
          },
          bodyAssetCount: data.length,
          assets: perAsset,
        })

        this.assets = data
      } catch (e) {
        console.warn('[FrontPageAssetStrip] request error', { url, error: e })
      }
    },
  },
}
</script>

<style scoped>
/* Full-bleed black behind strip + carousel (removes gray gutter from default v-row/v-col) */
.front-page-asset-strip__band {
  width: 100%;
  background-color: #000;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.front-page-asset-strip__row {
  margin: 0 !important;
  width: 100%;
  background-color: #000;
}

.front-page-asset-strip__intro {
  padding: 16px 8px 24px;
  text-align: center;
}

@media (min-width: 960px) {
  .front-page-asset-strip__intro {
    text-align: left;
    padding-right: 16px;
  }
}

.front-page-asset-strip__title {
  font-size: 1.5rem;
  margin: 0 0 12px;
  max-width: 100%;
  color: #1a1a1a;
}

.front-page-asset-strip__hint {
  margin: 0;
  font-size: 1rem;
  line-height: 1.45;
  color: #424242;
  max-width: 100%;
}

/* Carousel height from stripCarouselHeight computed */
.front-page-asset-strip {
  width: 100%;
  position: relative;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  background-color: #000;
  border-radius: 0;
  overflow: hidden;
}

.front-page-asset-strip__carousel {
  width: 100%;
  height: 100%;
  background-color: #000;
  box-shadow: none !important;
}

.front-page-asset-strip__carousel >>> .v-window {
  box-shadow: none !important;
  background-color: #000 !important;
}

.front-page-asset-strip__carousel >>> .v-window__container {
  background-color: #000;
}

.front-page-asset-strip__carousel >>> .v-carousel__item {
  background-color: #000;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.front-page-asset-strip__carousel-item {
  background-color: #000;
}

.front-page-asset-strip__slide-inner {
  height: 100%;
  width: 100%;
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  box-sizing: border-box;
  background-color: #000;
}

.front-page-asset-strip__image {
  display: block;
  line-height: 0;
  width: auto;
  max-width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  background-color: #000;
  margin-left: auto;
  margin-right: auto;
  align-self: center;
}

.front-page-asset-strip__image >>> canvas {
  display: block;
  vertical-align: top;
  max-width: 100%;
}

.front-page-asset-strip__carousel >>> .v-carousel__prev,
.front-page-asset-strip__carousel >>> .v-carousel__next {
  background: rgba(255, 255, 255, 0.92) !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.front-page-asset-strip__carousel >>> .v-carousel__controls {
  background: transparent;
}

.front-page-asset-strip__carousel >>> .v-carousel__delimiters {
  padding-bottom: 12px;
}
</style>
